import { useEffect } from 'react';
import { ArrowDownIcon, ArrowRightIcon } from 'lucide-react';
import { routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
import { ascentII, vikingLineup, vikingReasons } from '../data/viking';
const TITLE = 'Viking Spas in Brooklyn, Michigan | Pegasus Spas';
const DESCRIPTION = 'Explore the Viking Spas lineup in plain language, then visit Pegasus Spas in Brooklyn, Michigan for help comparing seating, size and setup.';
const CANONICAL = 'https://www.pegasus-spas.com/hot-tubs/viking-spas';
const ASCENT_IMAGE = ascentII.image;
const ASCENT_ALT = ascentII.alt;
const reasons = vikingReasons;
const lineup = vikingLineup;
const ascentSpecs = ascentII.specs;
const relatedLinks = [{
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Compare Sundance Spas',
  href: routes.sundance
}, {
  label: 'See Available Spas',
  href: routes.availableSpas
}, {
  label: 'Explore Financing',
  href: routes.financing
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function VikingSpasPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-2415" data-mp="2lk69nd0lan6p">
      {/* Editorial opening on a light field */}
      <section aria-labelledby="viking-h1" className="w-full bg-water" data-id="element-2416" data-mp="27jvhm6njn2jy">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-14" data-id="element-2417" data-mp="27lq1riqzqqmz">
          <div className="grid grid-cols-1 items-center gap-9 lg:grid-cols-12 lg:gap-12" data-id="element-2418" data-mp="2bkp734gmwpey">
            <div className="lg:col-span-6" data-id="element-2419" data-mp="i39m9woa6pwv">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pegasus-blue" data-id="element-2420" data-mp="dbrplktajbhp">
                Viking Spas at Pegasus
              </p>
              <h1 id="viking-h1" className="mt-3 font-display text-h1-mobile font-semibold text-ink lg:text-[48px] lg:leading-[56px]" data-id="element-2421" data-mp="8d7zr9bktfk2">
                Viking Spas, Explained Simply
              </h1>
              <p className="mt-5 text-body text-ink/80 sm:text-body-lg" data-id="element-2422" data-mp="w29ax4bsn4t1">
                Viking makes a broad range of American-built spas, from straightforward first-spa
                choices to more fully featured models. We help you compare the differences in
                plain language and choose what fits your household, your space and the way you plan
                to use it.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2423" data-mp="37racwpyuawaf">
                <a href="#viking-lineup" className="inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2424" data-mp="37u69a9v1hcwr">
                  Start With the Viking Lineup
                  <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-2425" data-mp="211php7cfol8h" />
                </a>
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2426" data-mp="260itmvc2o46j">
                  Visit the Brooklyn Showroom
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-6" data-id="element-2427" data-mp="knfqm5gt4idy">
              <figure className="relative" data-id="element-2428" data-mp="koqvk8lznw6n">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-card sm:aspect-[3/2]" data-id="element-2429" data-mp="plqzodza2ov4">
                  <img src={ASCENT_IMAGE} alt={ASCENT_ALT} className="h-full w-full object-cover object-[78%_62%] sm:object-[72%_58%]" decoding="async" data-id="element-2430" data-mp="3qgyjddbindm7" />
                </div>
                <figcaption className="pointer-events-none absolute bottom-3 right-3 rounded-[4px] bg-ink/75 px-2 py-[5px] text-[11px] font-medium leading-none text-white/90 sm:text-[12px]" data-id="element-2431" data-mp="47num9ty8buf">
                  Viking Ascent II | Elite Series
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Why Pegasus carries Viking */}
      <section aria-labelledby="why-heading" className="w-full bg-white" data-id="element-2432" data-mp="3himvvk5y4vxz">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2433" data-mp="3co8w75k942ni">
          <h2 id="why-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2434" data-mp="2oz7ip3wka1bd">
            Why Pegasus Carries Viking
          </h2>

          <dl className="mt-8 max-w-4xl divide-y divide-ink/15 border-y border-ink/15" data-id="element-2435" data-mp="zi2h4ah0goq3">
            {reasons.map(reason => <div key={reason.title} className="py-5 sm:grid sm:grid-cols-12 sm:gap-8" data-id="element-2436" data-mp="14b5605auf9zy">
                <dt className="font-display text-[20px] font-semibold leading-8 text-ink sm:col-span-4" data-id="element-2437" data-mp="pmimdre6nw2x">
                  {reason.title}
                </dt>
                <dd className="mt-1.5 text-[16.5px] leading-7 text-ink/75 sm:col-span-8 sm:mt-0" data-id="element-2438" data-mp="1i93cypmu445f">
                  {reason.body}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* The lineup */}
      <section id="viking-lineup" aria-labelledby="lineup-heading" className="w-full scroll-mt-28 border-y border-ink/10 bg-water" data-id="element-2439" data-mp="19azuk275bnhu">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2440" data-mp="3ty2yq6pnwdo3">
          <div className="max-w-3xl" data-id="element-2441" data-mp="1vv4qeegts36">
            <h2 id="lineup-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2442" data-mp="1wwpxbp73uym">
              The Viking Lineup at a Glance
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-2443" data-mp="1zl0idbehy2s5">
              Four product families, each with a different starting point.
            </p>
          </div>

          <ol className="mt-9 max-w-5xl" data-id="element-2444" data-mp="1psl00czafc6q">
            {lineup.map(family => <li key={family.name} className="border-t border-ink/20 py-7 last:border-b last:border-ink/20 lg:grid lg:grid-cols-12 lg:gap-8" data-id="element-2445" data-mp="1lsammf961jhu">
                <div className="flex items-baseline gap-4 lg:col-span-4 lg:block" data-id="element-2446" data-mp="1bueb3mcbgmj7">
                  <span aria-hidden="true" className="font-display text-[28px] font-semibold leading-none text-pegasus-magenta lg:text-[32px]" data-id="element-2447" data-mp="16us2xrbchwh9">
                    {family.number}
                  </span>
                  <h3 className="font-display text-[22px] font-semibold leading-8 text-ink lg:mt-2 lg:text-[26px]" data-id="element-2448" data-mp="316gjfxx8673r">
                    {family.name}
                  </h3>
                </div>

                <div className="mt-3 lg:col-span-8 lg:mt-0" data-id="element-2449" data-mp="3doiu8l7qu6cm">
                  <p className="max-w-2xl text-[16.5px] leading-7 text-ink/80 sm:text-body" data-id="element-2450" data-mp="3iiwub0xcp1nm">
                    {family.body}
                  </p>
                  <SiteLink to={family.href} className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 text-[16.5px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2451" data-mp="2qqfmwaoz7e2h">
                    {family.linkLabel}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2452" data-mp="f157s21zh9wt" />
                  </SiteLink>
                </div>
              </li>)}
          </ol>

          <p className="mt-6 max-w-3xl text-[16px] leading-7 text-ink/70" data-id="element-2453" data-mp="k22mu5rp5nu3">
            These are product families to explore, not a statement of what is on the Pegasus
            showroom floor. Ask us what we can show you and what we can order.
          </p>
        </div>
      </section>

      {/* Featured model */}
      <section aria-labelledby="ascent-heading" className="w-full bg-white" data-id="element-2454" data-mp="1a33nj72l983u">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2455" data-mp="1a10lfoo5voir">
          <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pine" data-id="element-2456" data-mp="1oqg92vsebdu8">
            Featured Viking model: Ascent II
          </p>
          <h2 id="ascent-heading" className="mt-2 max-w-3xl font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2457" data-mp="1xspmpuiynnbu">
            Meet the Ascent II
          </h2>

          <div className="mt-7 aspect-[21/9] w-full overflow-hidden rounded-card" data-id="element-2458" data-mp="3e54rpm0y5w5">
            <img src={ASCENT_IMAGE} alt={ASCENT_ALT} className="h-full w-full object-cover object-[82%_65%]" loading="lazy" decoding="async" data-id="element-2459" data-mp="3ukzk8shh7cwr" />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-2460" data-mp="2f0p35e1d606r">
            <div className="lg:col-span-7" data-id="element-2461" data-mp="21bevga7oq0a0">
              <p className="text-body leading-7 text-ink/85 sm:text-body-lg" data-id="element-2462" data-mp="218izgrfedlo7">
                The Ascent II uses an open, non-lounge layout that seats up to seven adults, so
                nobody is locked into one spot and people can move between seats as the evening goes
                on.
              </p>
              <p className="mt-4 text-body leading-7 text-ink/85 sm:text-body-lg" data-id="element-2463" data-mp="1w962veyq508f">
                That makes it a useful example for families or groups who would rather have flexible
                seating than a single reclined seat.
              </p>
            </div>

            <div className="lg:col-span-5" data-id="element-2464" data-mp="3cqadm65jijvb">
              <ul className="divide-y divide-ink/15 border-y border-ink/15" data-id="element-2465" data-mp="3gr3wum81fahv">
                {ascentSpecs.map(spec => <li key={spec} className="py-3 text-[17px] leading-7 text-ink/85" data-id="element-2466" data-mp="3b722h1ba4f31">
                    {spec}
                  </li>)}
              </ul>
              <p className="mt-4 text-[15.5px] leading-6 text-ink/65" data-id="element-2467" data-mp="31a73khb3gco8">
                Specifications from{' '}
                <a href={ascentII.source} target="_blank" rel="noopener noreferrer" className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-2468" data-mp="368h12ykajpd0">
                  Viking Spas
                </a>
                . We&rsquo;ll confirm the details and site requirements with you before you buy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Showroom close */}
      <section aria-labelledby="close-heading" className="w-full bg-white" data-id="element-2469" data-mp="29hg7q7xbopzi">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2470" data-mp="2dl4cyjyhf9hl">
          <div className="max-w-3xl" data-id="element-2471" data-mp="2600t66cbz12p">
            <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pine" data-id="element-2472" data-mp="25yz7z91loy79">
              See the Difference in Person
            </p>
            <h2 id="close-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-[34px] lg:leading-[42px]" data-id="element-2473" data-mp="2fzj9mct184q0">
              Find Your Favorite Seat. Try the Jets.
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-2474" data-mp="1nalrqasqg9xs">
              We have 10–12 showroom models to sit in and compare, plus three operating spas
              you can actually try. Stretch out, feel the jets and see which layout you enjoy most.
              We will help you compare the options.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2475" data-mp="14jb3y2ib0da4">
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2476" data-mp="14hhvqis1ll00">
              Come Try a Spa
            </SiteLink>
            <SiteLink to={routes.availableSpas} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2477" data-mp="1y17bmrzv77i2">
              See Available Spas
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-2478" data-mp="1fa6d252npdz1">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2479" data-mp="1k9iuubeo7uur">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-2480" data-mp="1k7prp639nsri">
            {relatedLinks.map(link => <li key={link.label} data-id="element-2481" data-mp="2vo6a98a4apil">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2482" data-mp="2vn4l5zw9o2hv">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2483" data-mp="5by8md31dmhm" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}